import { createBaseApi } from '../baseApi';
import { API_ENDPOINTS } from '../../../utils/config';

// Create base background image API
const backgroundImageApi = createBaseApi('backgroundImageApi');

// Enhanced API with background image endpoints
export const enhancedBackgroundImageApi = backgroundImageApi.injectEndpoints({
  endpoints: (builder) => ({
    // Get user's background images
    getUserBackgrounds: builder.query({
      query: ({ 
        limit = 20, 
        start = 0, 
        category, 
        active, 
        isPublic, 
        sort = 'createdAt:desc' 
      } = {}) => ({
        url: API_ENDPOINTS.BACKGROUND_IMAGE.BASE,
        method: 'GET',
        params: {
          _limit: limit,
          _start: start,
          category,
          active,
          public: isPublic,
          _sort: sort
        }
      }),
      providesTags: (result) => 
        result?.success && result?.data
          ? [
              'BackgroundImage',
              ...result.data.map(({ id }) => ({ type: 'BackgroundImage', id }))
            ]
          : ['BackgroundImage'],
      transformResponse: (response) => {
        if (response?.success && response?.data) {
          return {
            backgrounds: response.data.map(item => ({
              id: item.id,
              name: item.name,
              description: item.description,
              category: item.category,
              image: item.image ? {
                id: item.image.id,
                url: item.image.url
              } : null,
              thumbnail: item.thumbnail ? {
                id: item.thumbnail.id,
                url: item.thumbnail.url
              } : null,
              isPublic: item.isPublic,
              isActive: item.isActive,
              tags: item.tags || [],
              usageCount: item.usageCount || 0,
              lastUsed: item.lastUsed,
              createdAt: item.created_at,
              updatedAt: item.updated_at
            })),
            pagination: response.pagination
          };
        }
        return { backgrounds: [], pagination: { total: 0, hasMore: false } };
      }
    }),

    // Get public background images
    getPublicBackgrounds: builder.query({
      query: ({ 
        limit = 20, 
        start = 0, 
        category, 
        sort = 'usageCount:desc' 
      } = {}) => ({
        url: API_ENDPOINTS.BACKGROUND_IMAGE.PUBLIC_BACKGROUNDS,
        method: 'GET',
        params: {
          _limit: limit,
          _start: start,
          category,
          _sort: sort
        }
      }),
      providesTags: ['BackgroundImage'],
      transformResponse: (response) => {
        if (response?.success && response?.data) {
          return {
            backgrounds: response.data.map(item => ({
              id: item.id,
              name: item.name,
              description: item.description,
              category: item.category,
              image: item.image ? {
                id: item.image.id,
                url: item.image.url
              } : null,
              thumbnail: item.thumbnail ? {
                id: item.thumbnail.id,
                url: item.thumbnail.url
              } : null,
              isPublic: item.isPublic,
              tags: item.tags || [],
              usageCount: item.usageCount || 0,
              createdAt: item.created_at
            })),
            pagination: response.pagination
          };
        }
        return { backgrounds: [], pagination: { total: 0, hasMore: false } };
      }
    }),

    // Get background categories
    getBackgroundCategories: builder.query({
      query: () => ({
        url: API_ENDPOINTS.BACKGROUND_IMAGE.CATEGORIES,
        method: 'GET'
      }),
      transformResponse: (response) => {
        return response?.success ? response.data : [];
      }
    }),

    // Upload background image with file
    uploadBackground: builder.mutation({
      query: ({ file, name, description, category = 'custom', isPublic = false, tags, thumbnail }) => {
        const formData = new FormData();
        formData.append('image', file);
        formData.append('name', name);
        if (description) {
          formData.append('description', description);
        }
        formData.append('category', category);
        formData.append('isPublic', isPublic.toString());
        if (tags && Array.isArray(tags)) {
          formData.append('tags', JSON.stringify(tags));
        }
        if (thumbnail) {
          formData.append('thumbnail', thumbnail);
        }

        return {
          url: API_ENDPOINTS.BACKGROUND_IMAGE.UPLOAD,
          method: 'POST',
          body: formData
        };
      },
      invalidatesTags: ['BackgroundImage'],
      transformResponse: (response) => {
        if (response?.success && response?.data) {
          const item = response.data;
          return {
            id: item.id,
            name: item.name,
            description: item.description,
            category: item.category,
            image: item.image ? {
              id: item.image.id,
              url: item.image.url
            } : null,
            thumbnail: item.thumbnail ? {
              id: item.thumbnail.id,
              url: item.thumbnail.url
            } : null,
            isPublic: item.isPublic,
            isActive: item.isActive,
            tags: item.tags || [],
            usageCount: item.usageCount || 0,
            lastUsed: item.lastUsed,
            createdAt: item.created_at,
            updatedAt: item.updated_at
          };
        }
        throw new Error(response?.message || 'Upload failed');
      }
    }),

    // Create background image with existing file IDs
    createBackground: builder.mutation({
      query: ({ imageId, thumbnailId, name, description, category = 'custom', isPublic = false, tags }) => ({
        url: API_ENDPOINTS.BACKGROUND_IMAGE.BASE,
        method: 'POST',
        body: {
          imageId,
          thumbnailId,
          name,
          description,
          category,
          isPublic,
          tags
        }
      }),
      invalidatesTags: ['BackgroundImage'],
      transformResponse: (response) => {
        if (response?.success && response?.data) {
          const item = response.data;
          return {
            id: item.id,
            name: item.name,
            description: item.description,
            category: item.category,
            image: item.image ? {
              id: item.image.id,
              url: item.image.url
            } : null,
            thumbnail: item.thumbnail ? {
              id: item.thumbnail.id,
              url: item.thumbnail.url
            } : null,
            isPublic: item.isPublic,
            isActive: item.isActive,
            tags: item.tags || [],
            usageCount: item.usageCount || 0,
            lastUsed: item.lastUsed,
            createdAt: item.created_at,
            updatedAt: item.updated_at
          };
        }
        throw new Error(response?.message || 'Creation failed');
      }
    }),

    // Get specific background image by ID
    getBackgroundById: builder.query({
      query: (id) => ({
        url: API_ENDPOINTS.BACKGROUND_IMAGE.BY_ID(id),
        method: 'GET'
      }),
      providesTags: (result, error, id) => [{ type: 'BackgroundImage', id }],
      transformResponse: (response) => {
        if (response?.success && response?.data) {
          const item = response.data;
          return {
            id: item.id,
            name: item.name,
            description: item.description,
            category: item.category,
            image: item.image ? {
              id: item.image.id,
              url: item.image.url
            } : null,
            thumbnail: item.thumbnail ? {
              id: item.thumbnail.id,
              url: item.thumbnail.url
            } : null,
            isPublic: item.isPublic,
            isActive: item.isActive,
            tags: item.tags || [],
            usageCount: item.usageCount || 0,
            lastUsed: item.lastUsed,
            createdAt: item.created_at,
            updatedAt: item.updated_at
          };
        }
        return null;
      }
    }),

    // Update background image
    updateBackground: builder.mutation({
      query: ({ id, ...updates }) => ({
        url: API_ENDPOINTS.BACKGROUND_IMAGE.UPDATE(id),
        method: 'PUT',
        body: updates
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'BackgroundImage', id },
        'BackgroundImage'
      ],
      transformResponse: (response) => {
        if (response?.success && response?.data) {
          const item = response.data;
          return {
            id: item.id,
            name: item.name,
            description: item.description,
            category: item.category,
            image: item.image ? {
              id: item.image.id,
              url: item.image.url
            } : null,
            thumbnail: item.thumbnail ? {
              id: item.thumbnail.id,
              url: item.thumbnail.url
            } : null,
            isPublic: item.isPublic,
            isActive: item.isActive,
            tags: item.tags || [],
            usageCount: item.usageCount || 0,
            lastUsed: item.lastUsed,
            createdAt: item.created_at,
            updatedAt: item.updated_at
          };
        }
        throw new Error(response?.message || 'Update failed');
      }
    }),

    // Delete background image
    deleteBackground: builder.mutation({
      query: (id) => ({
        url: API_ENDPOINTS.BACKGROUND_IMAGE.DELETE(id),
        method: 'DELETE'
      }),
      invalidatesTags: (result, error, id) => [
        { type: 'BackgroundImage', id },
        'BackgroundImage'
      ]
    })
  })
});

// Export hooks for the background image API
export const {
  useGetUserBackgroundsQuery,
  useGetPublicBackgroundsQuery,
  useGetBackgroundCategoriesQuery,
  useUploadBackgroundMutation,
  useCreateBackgroundMutation,
  useGetBackgroundByIdQuery,
  useUpdateBackgroundMutation,
  useDeleteBackgroundMutation
} = enhancedBackgroundImageApi;

export default enhancedBackgroundImageApi;